import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { GALLERY, GALLERY_CATS, IMG } from "../data/content";
import { PageHero, useTitle, EASE } from "../components/shared";
import { ChevronLeft, ChevronRight, X, ArrowRight } from "lucide-react";

const CAT_KEY = {
  Haldi: "haldi",
  Wedding: "wedding",
  "Sangeet & Cocktail": "sangeet",
  "Beach Wedding": "beach",
  "Rishikesh River Wedding": "river",
  "Palace Wedding": "palace",
  "Destination Weddings": "destination",
  "Business Events": "business",
  "Social Events": "social",
};

export default function Gallery() {
  useTitle("Gallery | The Big Wedd — Real Wedding Photography");
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(-1);

  const items = useMemo(
    () => (cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === CAT_KEY[cat])),
    [cat]
  );

  const step = (dir) => setLightbox((p) => (p < 0 ? p : (p + dir + items.length) % items.length));

  return (
    <main>
      <PageHero
        eyebrow="Gallery"
        title={[<span key="t">Through <span className="italic text-gold-light">our lens</span></span>]}
        sub="Haldi, weddings, sangeet nights, beach and river celebrations — real moments from The Big Wedd's weddings."
        img={IMG.galleryHero}
      />

      <section className="bg-ivory py-24 md:py-32" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-3" data-testid="gallery-filters">
            {GALLERY_CATS.map((c) => (
              <button
                key={c}
                onClick={() => { setCat(c); setLightbox(-1); }}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full border px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${cat === c ? "border-emerald bg-emerald text-ivory" : "border-gold/40 text-stone2 hover:border-gold hover:text-gold-text"}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="gallery-masonry">
            <AnimatePresence mode="popLayout">
              {items.map((g, i) => (
                <motion.button
                  key={`${g.src}-${i}`}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.04, ease: EASE }}
                  onClick={() => setLightbox(i)}
                  data-testid={`gallery-item-${i}`}
                  className="group relative mb-5 block w-full break-inside-avoid overflow-hidden text-left"
                >
                  <img src={g.src} alt={g.label} loading="lazy" className="w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-emerald-deep/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <p className="p-5 font-serif text-lg italic text-ivory">{g.label}</p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
          {items.length === 0 && (
            <p className="mt-12 text-center text-sm text-stone2" data-testid="gallery-empty">New photographs are being added to this collection soon.</p>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox >= 0 && items[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-4 md:p-12"
            data-testid="gallery-lightbox"
            onClick={() => setLightbox(-1)}
          >
            <button className="absolute right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-ivory transition-colors hover:bg-gold hover:text-ink" data-testid="lightbox-close" onClick={() => setLightbox(-1)} aria-label="Close">
              <X className="h-5 w-5" />
            </button>
            <button className="absolute left-4 md:left-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-ivory transition-colors hover:bg-gold hover:text-ink" data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <motion.figure
              key={lightbox}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={items[lightbox].src} alt={items[lightbox].label} className="max-h-[76vh] max-w-full object-contain" />
              <figcaption className="mt-4 text-center">
                <p className="font-serif text-lg italic text-ivory/80">{items[lightbox].label} — {lightbox + 1} / {items.length}</p>
                {items[lightbox].story && (
                  <Link to={`/weddings/${items[lightbox].story}`} data-testid="lightbox-story-link" className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold-light hover:text-gold">
                    View the full story <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </figcaption>
            </motion.figure>
            <button className="absolute right-4 md:right-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-ivory transition-colors hover:bg-gold hover:text-ink" data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next">
              <ChevronRight className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
